import { ApiError, apiPatch, apiPost } from "@/shared/api";

/** Utente applicativo web-mode (login R5 — vedi backend /api/auth/*). */
export type AuthUser = {
  id: number;
  username: string;
  role: string;
  is_active: boolean;
  created_at: string;
  /** Secondo fattore TOTP attivo su questo account. */
  mfa_attiva?: boolean;
  /** Scadenza della password (rotazione annuale). ``null`` = non scade. */
  password_scade_il?: string | null;
};

type UserEnvelope = { user: AuthUser; password_scaduta?: boolean };

export type EsitoLogin = { user: AuthUser; passwordScaduta: boolean };

/** Configurazione del secondo fattore: il secret si vede una volta sola. */
export type ConfigurazioneMfa = {
  secret: string;
  secret_leggibile: string;
  uri: string;
};

/**
 * Autentica username+password (e il codice del secondo fattore, se attivo).
 * Al successo il backend imposta il cookie di sessione httpOnly ``om_session``
 * (stesso origin → il browser lo allega da solo alle chiamate successive).
 *
 * Errori: 400 credenziali non valide (detail in italiano), 429 rate limit,
 * 401 ``mfa_required`` quando la password è giusta ma manca il codice — quello
 * NON è un fallimento, si riconosce con ``isMfaRichiesta``.
 */
export async function login(
  username: string,
  password: string,
  codice?: string,
): Promise<EsitoLogin> {
  const body = await apiPost<UserEnvelope>("/api/auth/login", {
    username,
    password,
    ...(codice ? { codice } : {}),
  });
  return { user: body.user, passwordScaduta: Boolean(body.password_scaduta) };
}

/** ``true`` se il backend chiede il codice del secondo fattore. */
export function isMfaRichiesta(err: unknown): boolean {
  return err instanceof ApiError && err.code === "mfa_required";
}

/**
 * Avvia l'arruolamento del secondo fattore per l'utente collegato: torna il
 * secret da abbinare all'app di autenticazione. Il secret esce dall'API SOLO
 * qui, una volta: se si perde, si rilancia l'arruolamento da capo.
 * Chiede la password attuale: una sessione aperta da sola non basta.
 */
export async function mfaSetup(password: string): Promise<ConfigurazioneMfa> {
  return apiPost<ConfigurazioneMfa>("/api/auth/mfa/setup", { password });
}

/** Conferma l'arruolamento con un codice valido: da qui il TOTP è obbligatorio. */
export async function mfaConfirm(codice: string): Promise<AuthUser> {
  return apiPost<AuthUser>("/api/auth/mfa/confirm", { codice });
}

/** Azzera il secondo fattore di un utente (telefono perso). Solo amministratore. */
export async function mfaReset(id: number): Promise<AuthUser> {
  return apiPost<AuthUser>(`/api/auth/users/${id}/mfa/reset`);
}

/** Termina la sessione corrente: il backend cancella il cookie. */
export async function logout(): Promise<void> {
  await apiPost<{ ok: boolean }>("/api/auth/logout");
}

export async function createUser(username: string, password: string): Promise<AuthUser> {
  return apiPost<AuthUser>("/api/auth/users", { username, password });
}

/**
 * Attiva/disattiva un utente. Il backend rifiuta la disattivazione
 * dell'ultimo utente attivo: l'errore va mostrato all'operatore.
 */
export async function setUserActive(id: number, isActive: boolean): Promise<void> {
  await apiPatch<unknown>(`/api/auth/users/${id}/active`, { is_active: isActive });
}

/**
 * Cambia la password di un utente. Richiede la password ATTUALE dell'utente
 * che sta agendo (re-auth lato backend): una sessione aperta/rubata non basta
 * a resettare una password. Il backend risponde 400 "Password corrente non
 * valida." se ``currentPassword`` non combacia.
 */
export async function changeUserPassword(
  id: number,
  currentPassword: string | null,
  newPassword: string,
): Promise<void> {
  await apiPatch<unknown>(`/api/auth/users/${id}/password`, {
    ...(currentPassword ? { current_password: currentPassword } : {}),
    new_password: newPassword,
  });
}
